import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import type { DunningReviewRow } from '@staff-app/shared-types';

const REASON_MAX = 500;

/**
 * The Deactivate (and, since 2026-09-23, Reactivate) confirmation for a Dunning Review row — the SAME AlertDialog shape as the
 * Dunning Exclusion dialog (title, explanation, a required note, Cancel + action), replacing the
 * browser `window.confirm` + `window.prompt` pair (requester, 2026-09-23).
 *
 * The reason is REQUIRED because it is stored against the episode and in the review row: it is
 * the audit answer to "why was this account switched off". On failure the dialog stays open and
 * shows the server's message, so the operator can retry without re-typing it.
 */
export function DeactivateDialog({
  row,
  onClose,
  onConfirm,
  mode = 'deactivate',
}: {
  /** Null = closed. */
  row: DunningReviewRow | null;
  /**
   * `reactivate` (2026-09-23): the same dialog for the reverse decision — screens and portal
   * come back, and the account returns to awaiting a decision (the invoice is still unpaid).
   */
  mode?: 'deactivate' | 'reactivate';
  onClose: () => void;
  /** Resolves when the decision was applied (or not needed); throws with the server's message. */
  onConfirm: (row: DunningReviewRow, reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (row) {
      setReason('');
      setBusy(false);
      setError(null);
    }
  }, [row]);

  const who = row ? (row.companyName ?? row.accountId) : '';
  const re = mode === 'reactivate';
  const tid = re ? 'dunning-reactivate' : 'dunning-deactivate';
  const screens = row?.screenCount != null ? `All ${row.screenCount}` : 'All their';
  const trimmed = reason.trim();
  const valid = trimmed.length > 0 && trimmed.length <= REASON_MAX;

  const submit = async () => {
    if (!row || !valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(row, trimmed);
      onClose();
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={row !== null} onOpenChange={(o) => (o || busy ? null : onClose())}>
      <AlertDialogContent data-testid={`${tid}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{re ? `Reactivate ${who}?` : `Deactivate ${who}?`}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            {re ? (
              <div className="flex flex-col gap-2 text-xs">
                <span>
                  {
                    'Screens come back and the account can sign in to the portal again, straight away.'
                  }
                </span>
                <span>
                  {
                    'The invoice is still unpaid, so the account goes back to "Awaiting decision" — it can be deactivated again later, and paying still ends the dunning. Stripe is not changed.'
                  }
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-2 text-xs">
                <span>
                  {`${screens} screens turn off: their displays show the on-hold notice, and the account is locked out of the portal until payment clears.`}
                </span>
                <span>
                  {
                    'Nothing is deleted — playback and portal access resume automatically the moment they pay. Stripe is not changed.'
                  }
                </span>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-1">
          <Label htmlFor={`${tid}-reason`}>{'Reason'}</Label>
          <Input
            id={`${tid}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              re
                ? 'e.g. promised payment by Friday'
                : 'e.g. 45 days past due, no response to notices'
            }
            data-testid={`${tid}-reason`}
          />
          <p className="text-[11px] text-muted-foreground">
            {`Required — saved with the decision so the account's history says why it was ${re ? 'reactivated' : 'deactivated'}. ${trimmed.length}/${REASON_MAX}`}
          </p>
          {error ? (
            <p className="text-xs text-destructive" data-testid={`${tid}-error`}>
              {error}
            </p>
          ) : null}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`${tid}-cancel`}>
            {'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!valid || busy}
            onClick={(e) => {
              e.preventDefault();
              void submit();
            }}
            className={
              re ? undefined : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            }
            data-testid={`${tid}-submit`}
          >
            {busy ? (re ? 'Reactivating…' : 'Deactivating…') : re ? 'Reactivate' : 'Deactivate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
